import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS } from "../data/content";
import { useTheme } from "../context/ThemeContext";
import { WhatsAppNavLink } from "./WhatsAppButton";
import logo from "../assets/logo.png";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow,padding] duration-500 ${
        scrolled ? "glass py-3 shadow-[0_8px_32px_rgba(0,0,0,0.18)]" : "py-5 bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <span
            className="grid place-items-center w-12 h-12 shrink-0 rounded-full p-1.5
              bg-white shadow-[0_4px_20px_rgba(44,76,59,0.18),0_1px_3px_rgba(0,0,0,0.08)]
              ring-1 ring-black/[0.06]
              dark:shadow-[0_4px_22px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.08)]
              group-hover:scale-105 group-hover:shadow-[0_6px_24px_rgba(44,76,59,0.22)]
              dark:group-hover:shadow-[0_6px_28px_rgba(0,0,0,0.55)]
              transition-[transform,box-shadow] duration-300"
          >
            <img src={logo} alt="Kalpavriksha logo" className="w-full h-full object-contain" data-testid="logo-image" />
          </span>
          <span className="leading-none">
            <span className="block font-serif-display text-xl tracking-tight">Kalpavriksha</span>
            <span className="label-caps text-[0.55rem] text-muted-foreground">Interiors & Constructions</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-0.5 xl:gap-1">
          {NAV_LINKS.map((l) => {
            const text = l.navLabel || l.label;
            const navId = l.to === "/" ? "home" : l.to.slice(1).replace(/\//g, "-");
            return (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${navId}`}
              className={`relative whitespace-nowrap px-2.5 xl:px-3 py-2 text-[13px] xl:text-sm font-medium tracking-tight transition-colors duration-300 hover:text-accent ${
                pathname === l.to ? "text-accent" : "text-foreground/80"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                {text}
              </span>
              {pathname === l.to && (
                <motion.span layoutId="navdot" className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-1 h-1 rounded-full bg-accent" />
              )}
            </Link>
          );})}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="grid place-items-center w-10 h-10 rounded-full border border-border hover:border-accent transition-colors duration-300"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <WhatsAppNavLink className="hidden md:inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 bg-[#25D366]/10 text-[#128C7E] dark:text-[#25D366] px-4 py-2.5 text-sm font-medium hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors duration-300" />
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors duration-300"
          >
            Get a Quote
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-border"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden glass mt-3 mx-4 rounded-2xl"
          >
            <div className="flex flex-col p-4">
              {NAV_LINKS.map((l) => {
                const navId = l.to === "/" ? "home" : l.to.slice(1).replace(/\//g, "-");
                return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${navId}`}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 text-base border-b border-border/50 last:border-0 hover:text-accent transition-colors"
                >
                  {l.label}
                </Link>
              );})}
              <div className="pt-3 mt-1 border-t border-border/50 flex flex-col gap-3 px-4 pb-2">
                <WhatsAppNavLink
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white py-3 text-sm font-medium"
                  onClick={() => setOpen(false)}
                />
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  className="text-center rounded-full bg-primary text-primary-foreground py-3 text-sm font-medium"
                >
                  Get a Quote
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
